import React, { useEffect } from 'react'
import { Alert, Box, Button, Checkbox, ClickAwayListener, Chip, Drawer, FormControlLabel, IconButton, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { formatDuration } from '../model/durationFormat'
import { SUSPICIOUS_THRESHOLD_MS } from '../model/timelineView'
import { actsOf, actsPhrase } from '../model/closeNowMode'
import { canCorrectJobState, edgeOf, windowOf } from '../model/correctionDraft'
import { parseIsoInstant } from '../model/timeGrid'
import PreviewSummary from './PreviewSummary'
import UiMessageAlert from './UiMessageAlert'

// The panel «Correct intervention» (RN42-RN74, maqueta .panel): a Drawer on the right with the data of the intervention, the form
// (kind, time, tapes, «Back at…», reason, comment), the preview and the apply button. It only draws what `useCorrection` holds.
const PANEL_WIDTH = 'min(440px, 100%)'
const KINDS = [
  { kind: 'ADJUST_END', label: 'Adjust end' },
  { kind: 'ADJUST_START', label: 'Adjust start' },
  { kind: 'VOID', label: 'Void' }
]

const toggle = (tapes, tape, on) => (on ? [...tapes, tape] : tapes.filter((candidate) => candidate !== tape))

const DataList = ({ rows }) => (
  <Box component='dl' sx={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', gap: '4px 10px', fontSize: 13, m: 0 }}>
    {rows.map(({ label, value }) => (
      <React.Fragment key={label}>
        <Box component='dt' sx={{ color: 'text.secondary' }}>{label}</Box>
        <Box component='dd' sx={{ m: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{value}</Box>
      </React.Fragment>
    ))}
  </Box>
)

const CorrectionPanel = ({ block, correction, loaded, reasons, reasonsFailed, onClose, onReload, onOpenCorrection }) => {
  const { t_ } = useNamesTranslation()
  const { formatDateTime } = useDateFormatter()
  const { intervention, draft, applying } = correction
  // The form cannot change while applying (RN66) nor once the reading lost the intervention after an apply (read-only, A202-23).
  const locked = applying || correction.readOnly
  const raw = block.raw
  const worker = raw.workers.find((candidate) => candidate.id === intervention.worker.id)
  const workerName = correction.workerName
  const deviceName = raw.device.name || `#${raw.device.id}`
  const byJob = intervention.job !== null && intervention.job !== undefined
  const startMs = parseIsoInstant(intervention.start).getTime()
  const isOpen = intervention.open === true
  // An open intervention carries the `now` of the reading as its end (RN20, `withRecordedEnd`).
  const endMs = parseIsoInstant(intervention.end).getTime()
  const durationMs = endMs - startMs
  const others = intervention.otherWorkersOnJob.map((other) => correction.names.worker(other.id))
  const modeChip = raw.device.mode === 'OFFLINE' ? t_('Manual machine') : raw.device.mode === 'AUTO' ? t_('Automatic machine') : ''

  const rows = [
    { label: t_('Worker'), value: worker ? `${workerName} (${worker.code})` : workerName },
    { label: t_('Machine'), value: <>{deviceName}{modeChip && <Chip size='small' label={modeChip} />}</> },
    byJob
      ? { label: t_('Job'), value: `${intervention.job.name || intervention.job.code}${intervention.fabricationOrder ? ` · ${intervention.fabricationOrder.code}` : ''}` }
      : { label: t_('Record'), value: t_('Presence on the machine') },
    { label: t_('Start'), value: formatDateTime(startMs) },
    { label: t_('End'), value: isOpen ? <Chip size='small' label={t_('in progress')} /> : formatDateTime(endMs) },
    {
      label: t_('Duration'),
      value: (
        <>
          {formatDuration(durationMs, t_)}
          {durationMs > SUSPICIOUS_THRESHOLD_MS && <Chip size='small' color='error' label={t_('more than 10 h')} />}
          {/* #3068: the chip opens the latest correction of the intervention in the history (when the page offers it);
              Sun sends `correctionIds` most recent first (#189 S54). */}
          {intervention.correctionIds.length > 0 && (
            <Chip
              size='small' color='info' label={t_('already corrected')} data-testid='already-corrected'
              disabled={applying}
              onClick={onOpenCorrection ? () => onOpenCorrection(intervention.correctionIds[0]) : undefined}
            />
          )}
        </>
      )
    }
  ]
  if (others.length > 0) rows.push({ label: t_('With'), value: `${others.join(', ')} ${t_('in the same job')}` })

  // A panel that is applying cannot be closed by any means (RN66).
  const handleClose = () => { if (!applying) onClose() }
  // The panel does not hide the timeline (persistent Drawer, no scrim): the handles are dragged with the panel open (RN40). So
  // «outside» is decided here: a click anywhere but the panel, an interactive segment (it selects another intervention) or a
  // handle closes it, and so does Esc (RN42).
  const handleClickAway = (event) => {
    if (event.target && event.target.closest && event.target.closest('.dc-seg[role="button"], [data-handle]')) return
    handleClose()
  }
  useEffect(() => {
    const handleKeyDown = (event) => { if (event.key === 'Escape') handleClose() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  })
  const handleAction = (action) => {
    if (action === 'PRESENCE_ONLY') correction.correctOnlyPresence()
    if (action === 'RELOAD_TIMELINE') onReload()
    if (action === 'TRY_AGAIN') correction.preview.tryAgain()
  }
  const handleApplyAction = (action) => {
    if (action === 'TRY_AGAIN') correction.applyAgain()
    else handleAction(action)
  }

  const edge = edgeOf(draft.kind, intervention)
  const window = windowOf(draft, intervention)
  const otherNames = correction.preview.vigent
    ? correction.preview.result.otherWorkersInWindow.onJob.map(correction.names.worker)
    : others
  const gate = correction.gate
  const isVoid = draft.kind === 'VOID'
  // RN27: in «close now» mode the button says what is going to happen (D-S10).
  const applyLabel = correction.closeNowMode
    ? (isVoid ? t_('Close now and void') : t_('Close now and trim'))
    : (isVoid ? t_('Void intervention') : t_('Apply correction'))

  return (
    <Drawer
      anchor='right'
      variant='persistent'
      open
      PaperProps={{ 'data-testid': 'correction-panel', 'data-anchor': `${intervention.anchor.table}:${intervention.anchor.rowId}`, 'data-worker': String(intervention.worker.id), 'data-width': PANEL_WIDTH, sx: { width: PANEL_WIDTH } }}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='h6' component='h2'>{t_('Correct intervention · {{worker}}', { worker: workerName })}</Typography>
              <Typography variant='body2' color='text.secondary'>
                {byJob ? `${deviceName} · ${intervention.job.name || intervention.job.code}${intervention.fabricationOrder ? ` (${intervention.fabricationOrder.code})` : ''}` : `${deviceName} · ${t_('presence on the machine')}`}
              </Typography>
            </Box>
            <IconButton aria-label={t_('Close')} onClick={handleClose} disabled={applying} sx={{ ml: 'auto' }}><Close /></IconButton>
          </Box>

          <Box sx={{ p: 2, display: 'grid', gap: 2, alignContent: 'start', overflowY: 'auto', flex: 1 }}>
            <DataList rows={rows} />

            <>
              <Box>
                <Typography variant='overline' component='h3' color='text.secondary'>{t_('What do you want to correct')}</Typography>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={draft.kind}
                  aria-label={t_('What do you want to correct')}
                  disabled={locked}
                  onChange={(_event, kind) => { if (kind) correction.setKind(kind) }}
                >
                  {KINDS.map(({ kind, label }) => (
                    <ToggleButton
                      key={kind}
                      value={kind}
                      disabled={locked || !intervention.allowedKinds.includes(kind) || (kind !== 'VOID' && !correction.adjustable)}
                    >
                      {t_(label)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {!correction.adjustable && <Alert severity='info'>{t_('This intervention is too short to adjust in 5-minute steps, it can only be voided.')}</Alert>}

              {isVoid
                ? <Alert severity='info'>{t_('The whole intervention is voided on the chosen tapes and no longer counts as work of {{worker}}. The original is kept in the correction.', { worker: workerName })}</Alert>
                : correction.adjustable && (
                  <DateTimePicker
                    label={draft.kind === 'ADJUST_END' ? t_('Real end time') : t_('Real start time')}
                    value={Number.isFinite(draft.newInstant) ? new Date(draft.newInstant) : null}
                    onChange={(date) => correction.setTyped(date ? date.getTime() : Number.NaN)}
                    minutesStep={5}
                    minDateTime={new Date(startMs)}
                    maxDateTime={new Date(endMs)}
                    disabled={locked}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: correction.timeInError, helperText: isOpen ? t_('Recorded: still open. You can also drag the edge on the timeline, inwards only.') : t_('Recorded {{date}}. You can also drag the edge on the timeline, inwards only.', { date: formatDateTime(edge) }) } }}
                  />
                )}

              {correction.noChange && !isVoid && correction.adjustable && (
                <Alert severity='info'>{t_('Move the time (or drag the edge) to see the effect of the correction.')}</Alert>
              )}

              {correction.errors.map((error) => <Alert key={error.key} severity='error' role='alert'>{t_(error.key, error.params)}</Alert>)}

              <Box>
                <Typography variant='overline' component='h3' color='text.secondary'>{t_('Tapes to correct')}</Typography>
                <Box sx={{ display: 'grid' }}>
                  <FormControlLabel
                    disabled={locked || !canCorrectJobState(intervention)}
                    control={<Checkbox checked={draft.tapes.includes('JOB_STATE')} onChange={(event) => correction.setTapes(toggle(draft.tapes, 'JOB_STATE', event.target.checked))} />}
                    label={raw.device.mode === 'OFFLINE' ? t_('Job state and machine state') : t_('Job state')}
                  />
                  <FormControlLabel
                    disabled={locked}
                    control={<Checkbox checked={draft.tapes.includes('PRESENCE')} onChange={(event) => correction.setTapes(toggle(draft.tapes, 'PRESENCE', event.target.checked))} />}
                    label={t_('Presence of {{worker}}', { worker: workerName })}
                  />
                </Box>
                <Typography variant='caption' color='text.secondary' component='p' sx={{ m: 0 }}>{t_('The clock-ins of {{worker}} are always recalculated with the Sun rule.', { worker: workerName })}</Typography>
                {otherNames.length > 0 && (
                  <Typography variant='caption' color='text.secondary' component='p' sx={{ m: 0 }}>{t_('{{workers}} also worked in the stretch you are cutting, so by default only the presence is corrected.', { workers: otherNames.join(', ') })}</Typography>
                )}
              </Box>

              {correction.resumeVisible && (
                <DateTimePicker
                  label={t_('Back at…')}
                  value={draft.resumeAt !== 0 ? new Date(draft.resumeAt) : null}
                  onChange={(date) => correction.setResumeAt(date ? date.getTime() : 0)}
                  minutesStep={5}
                  disabled={locked}
                  slotProps={{ textField: { size: 'small', fullWidth: true, helperText: t_('Optional. Leave it empty if the worker came back at the end of the stretch ({{date}}).', { date: formatDateTime(window.endMs) }) } }}
                />
              )}

              <PreviewSummary correction={correction} loaded={loaded} onAction={handleAction} />

              {correction.closeNowMode && (
                <Box data-testid='close-now-note' sx={{ display: 'grid', gap: 0.75, fontSize: 13 }}>
                  <Typography variant='body2'>
                    <b>{t_('This intervention is still open.')}</b> {t_('It will be done in two linked steps, in a single operation:')}
                  </Typography>
                  <Box component='ol' sx={{ m: 0, pl: 2.5 }}>
                    <li>{t_('Close now with the normal Sun operations: {{acts}}. A snapshot is taken.', { acts: actsPhrase(actsOf(correction.preview.vigent ? correction.preview.result : {}, draft.tapes), { t_, workerName }) })}</li>
                    <li>{isVoid ? t_('Void the whole intervention.') : t_('Trim from the real time until now.')}</li>
                  </Box>
                  <FormControlLabel
                    disabled={locked}
                    control={<Checkbox checked={correction.closeNowConfirmed} onChange={(event) => correction.setCloseNowConfirmed(event.target.checked)} />}
                    label={t_('I confirm that it is closed now')}
                  />
                </Box>
              )}

              <TextField
                select
                size='small'
                label={t_('Reason (required)')}
                value={draft.reasonCode}
                disabled={locked}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                error={Boolean(correction.fieldFailure.reason.key)}
                helperText={correction.fieldFailure.reason.key ? t_(correction.fieldFailure.reason.key, correction.fieldFailure.reason.params) : undefined}
                onChange={(event) => correction.setReason(event.target.value)}
              >
                <option value=''>{t_('Choose a reason…')}</option>
                {reasons.map((reason) => <option key={reason.code} value={reason.code}>{t_(reason.label)}</option>)}
              </TextField>

              <TextField
                multiline
                minRows={2}
                size='small'
                label={correction.reason && correction.reason.requiresComment ? t_('Comment (required with this reason)') : t_('Comment (optional)')}
                placeholder={t_('For example, according to the shift report the worker left at ten past three, confirmed with the shift lead.')}
                value={draft.comment}
                disabled={locked}
                InputLabelProps={{ shrink: true }}
                error={(!correction.comment.valid && draft.comment !== '') || Boolean(correction.fieldFailure.comment.key)}
                helperText={correction.fieldFailure.comment.key
                  ? t_(correction.fieldFailure.comment.key, correction.fieldFailure.comment.params)
                  : t_('{{count}} / {{max}} characters', { count: correction.comment.count, max: correction.comment.max })}
                onChange={(event) => correction.setComment(event.target.value)}
              />

              {correction.applyFailure.key && <UiMessageAlert message={correction.applyFailure} onAction={handleApplyAction} disabled={locked} />}
              {reasonsFailed && <Alert severity='warning'>{t_('The reasons could not be loaded, so corrections cannot be applied.')}</Alert>}
              <Typography variant='caption' color='text.secondary'>{t_('When applying, a snapshot of each record before and after is saved, with your user, the time and the reason.')}</Typography>
            </>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant='outlined' disabled={applying} onClick={handleClose}>{t_('Cancel')}</Button>
            <Button variant='contained' color={isVoid ? 'error' : 'primary'} disabled={!gate.enabled} onClick={() => correction.apply()}>
              {applying ? t_('Applying…') : applyLabel}
            </Button>
          </Box>
        </Box>
      </ClickAwayListener>
    </Drawer>
  )
}

export default CorrectionPanel
